"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useActionState, useEffect, useRef, useTransition } from "react";
import { formOptions, initialFormState, mergeForm, useForm, useStore, useTransform } from "@tanstack/react-form-nextjs";
import posthog from "posthog-js";
import { sanityFormSubmission } from "@/lib/actions/form-submission";
import { pushEvent } from "@/lib/analytics";
import { HoneypotField } from "@/components/form/honeypot-field.component";
import { useStalledSubmission } from "@/lib/hooks/use-stalled-submission";
import { HONEYPOT_FIELD } from "@/lib/honeypot";
import { FormDataFromQuery, FormFieldFromQuery, FormFieldGroupFromQuery } from "@/lib/types";
import { Button } from "@repo/ui/button";
import { ErrorMessage } from "@repo/ui/FormComponents/ErrorMessage";
import { FormFieldRenderer } from "./form-field-renderer.component";
import { FormFieldGroup } from "./form-field-group.component";
import { FormSuccess } from "./form-success.component";
import { RichTextRight } from "@/components/common/rich-text/rich-text-right.component";
import { commonValidation, generateDefaultFormValues, type FormFieldValue } from "@/lib/form-helpers";
export interface SanityFormProps {
  formData: FormDataFromQuery;
  /** Hide field group headlines (e.g. when rendered inside an accordion that already shows the heading) */
  hideGroupHeadlines?: boolean;
  /** Render the compact success state (small / inline forms like the footer newsletter signup) */
  compact?: boolean;
}
export const SanityForm = t0 => {
  const $ = _c(44);
  const {
    formData,
    hideGroupHeadlines: t1,
    compact: t2
  } = t0;
  const hideGroupHeadlines = t1 === undefined ? false : t1;
  const compact = t2 === undefined ? false : t2;
  const [state, action] = useActionState(sanityFormSubmission, initialFormState);
  const [isPending, startTransition] = useTransition();
  const hasStalled = useStalledSubmission(isPending);
  const honeypotRef = useRef(null);
  let t3;
  if ($[0] !== formData) {
    t3 = formOptions({
      defaultValues: generateDefaultFormValues(formData)
    });
    $[0] = formData;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const formOpts = t3;
  let t4;
  let t5;
  if ($[2] !== state) {
    t4 = baseForm => mergeForm(baseForm, state ?? {});
    t5 = [state];
    $[2] = state;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  const t6 = useTransform(t4, t5);
  let t7;
  if ($[5] !== formData._id) {
    t7 = async t8 => {
      const {
        value
      } = t8;
      const formDataToSubmit = new FormData();
      if (formData._id) {
        formDataToSubmit.append("formId", formData._id);
      }
      const honeypotValue = honeypotRef.current?.value.trim();
      if (honeypotValue) {
        formDataToSubmit.append(HONEYPOT_FIELD, honeypotValue);
      }
      Object.entries(value).forEach(t9 => {
        const [key, val] = t9;
        if (val !== null && val !== undefined) {
          if (Array.isArray(val)) {
            const hasObjects = val.some(_temp);
            if (hasObjects) {
              formDataToSubmit.append(key, JSON.stringify(val));
            } else {
              val.forEach(item_0 => formDataToSubmit.append(key, String(item_0)));
            }
          } else {
            formDataToSubmit.append(key, String(val));
          }
        }
      });
      startTransition(() => {
        action(formDataToSubmit);
      });
    };
    $[5] = formData._id;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== formOpts || $[8] !== t6 || $[9] !== t7) {
    t8 = {
      ...formOpts,
      transform: t6,
      onSubmit: t7
    };
    $[7] = formOpts;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const form = useForm(t8);
  const formErrors = useStore(form.store, _temp2);
  const isSubmissionSuccessful = !state && form.state.isSubmitted;
  const successMessage = formData.successMessage;
  const hasTrackedSubmit = useRef(false);
  let t10;
  let t9;
  if ($[11] !== formData._id || $[12] !== formData.name || $[13] !== formData.newsletterSubscribe || $[14] !== isSubmissionSuccessful) {
    t9 = () => {
      if (!isSubmissionSuccessful) {
        hasTrackedSubmit.current = false;
        return;
      }
      if (!hasTrackedSubmit.current) {
        hasTrackedSubmit.current = true;
        const formType = formData.newsletterSubscribe ? "newsletter" : "enquiry";
        pushEvent({
          event: "form_submit",
          form_id: formData._id,
          form_name: formData.name ?? undefined,
          form_type: formType
        });
        posthog.capture("form_submit", {
          form_id: formData._id,
          form_name: formData.name ?? undefined,
          form_type: formType
        });
      }
    };
    t10 = [isSubmissionSuccessful, formData._id, formData.name, formData.newsletterSubscribe];
    $[11] = formData._id;
    $[12] = formData.name;
    $[13] = formData.newsletterSubscribe;
    $[14] = isSubmissionSuccessful;
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  useEffect(t9, t10);
  let t11;
  if ($[17] !== form.Field || $[18] !== formData._id) {
    t11 = field => {
      if (!field.fieldName) {
        return <p>Field has no name</p>;
      }
      return <form.Field key={field.fieldName} name={field.fieldName} validators={commonValidation(field)}>{fieldProps => <FormFieldRenderer field={field} formId={formData._id} fieldApi={{
          name: fieldProps.name,
          value: fieldProps.state.value,
          handleChange: value_0 => fieldProps.handleChange(() => value_0 as FormFieldValue),
          handleBlur: fieldProps.handleBlur,
          state: {
            meta: {
              errors: fieldProps.state.meta.errors.filter(Boolean) as string[]
            }
          }
        }} />}</form.Field>;
    };
    $[17] = form.Field;
    $[18] = formData._id;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const renderField = t11;
  let t12;
  if ($[20] !== compact || $[21] !== form || $[22] !== formData.hideSuccessButton || $[23] !== formData.successAction || $[24] !== formData.successButtonLabel || $[25] !== formData.successHeading || $[26] !== formData.successImage || $[27] !== isSubmissionSuccessful || $[28] !== successMessage) {
    t12 = isSubmissionSuccessful && <FormSuccess heading={formData.successHeading} message={successMessage} image={formData.successImage} buttonLabel={formData.successButtonLabel} onStartOver={() => form.reset()} action={formData.successAction} hideButton={formData.hideSuccessButton ?? false} compact={compact} testId="sanity-form-success" />;
    $[20] = compact;
    $[21] = form;
    $[22] = formData.hideSuccessButton;
    $[23] = formData.successAction;
    $[24] = formData.successButtonLabel;
    $[25] = formData.successHeading;
    $[26] = formData.successImage;
    $[27] = isSubmissionSuccessful;
    $[28] = successMessage;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== form || $[31] !== formData._id || $[32] !== formData.fieldGroups || $[33] !== formData.submitLabel || $[34] !== formErrors || $[35] !== hasStalled || $[36] !== hideGroupHeadlines || $[37] !== isPending || $[38] !== isSubmissionSuccessful || $[39] !== renderField) {
    t13 = !isSubmissionSuccessful && <form noValidate={true} onSubmit={e => {
      e.preventDefault();
      form.handleSubmit(e);
    }}>{formData._id && <input type="hidden" name="formId" value={formData._id} />}<HoneypotField ref={honeypotRef} /><div className="flex flex-col gap-4 tablet:gap-6">{formData.fieldGroups?.map((item_1, itemIndex) => {
          if (item_1._type === "formTextBlock") {
            const content = (item_1 as {
              content?: unknown;
            }).content;
            if (!Array.isArray(content) || content.length === 0) {
              return null;
            }
            return <RichTextRight key={itemIndex} richText={content} as="form" className="text-text-primary" />;
          }
          const group = item_1 as FormFieldGroupFromQuery;
          return <FormFieldGroup key={itemIndex} headline={hideGroupHeadlines ? null : group.headline} description={group.description} numberOfColumns={group.numberOfColumns}>{group.fields?.map(field_0 => renderField(field_0))}</FormFieldGroup>;
        })}</div>{formErrors.map(_temp3)}{hasStalled && <ErrorMessage error="We couldn't reach the server. Please check your connection and try again." />}<div className="mt-6"><form.Subscribe selector={_temp4}>{t14 => {
            const {
              canSubmit,
              isSubmitting
            } = t14;
            return <Button variant="blue-filled" type="submit" disabled={(!canSubmit || isSubmitting || isPending) && !hasStalled} className="min-w-32 disabled:!text-button-primary-text">{(isSubmitting || isPending) && !hasStalled ? "Submitting..." : formData.submitLabel || "Submit"}</Button>;
          }}</form.Subscribe></div></form>;
    $[30] = form;
    $[31] = formData._id;
    $[32] = formData.fieldGroups;
    $[33] = formData.submitLabel;
    $[34] = formErrors;
    $[35] = hasStalled;
    $[36] = hideGroupHeadlines;
    $[37] = isPending;
    $[38] = isSubmissionSuccessful;
    $[39] = renderField;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t12 || $[42] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
  } else {
    t14 = $[43];
  }
  return t14;
};
function _temp(item) {
  return item !== null && typeof item === "object";
}
function _temp2(formState) {
  return formState.errors;
}
function _temp3(error) {
  return <ErrorMessage key={error as unknown as string} error={error} />;
}
function _temp4(formState_0) {
  return {
    canSubmit: formState_0.canSubmit,
    isSubmitting: formState_0.isSubmitting
  };
}